import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import Button from '../common/Button.js';

/**
 * Required note modal for destination unload, revert-to-load, or remove from request.
 */
const OgclUnloadNoteModal = ({
  open,
  action = 'unload', // 'unload' | 'revert' | 'remove' | 'note'
  shipmentIds = [],
  loading = false,
  onClose,
  onConfirm,
}) => {
  const [note, setNote] = useState('');
  const ids = Array.isArray(shipmentIds) ? shipmentIds.filter(Boolean) : [];
  const noteReady = note.trim().length > 0;

  useEffect(() => {
    if (open) setNote('');
  }, [open, action, ids.join('|')]);

  if (!open) return null;

  const copy = {
    unload: {
      title: 'Unload shipments',
      confirmLabel: 'Unload',
      variant: 'primary',
      body: 'Unload received GCs at this hub. The booking agent is notified with your note.',
      placeholder: 'Unload note (required)',
    },
    revert: {
      title: 'Revert unload',
      confirmLabel: 'Revert',
      variant: 'primary',
      body: 'This returns the GC from unloaded back to pending unload (load). It stays on this ITCL request.',
      placeholder: 'Why is unload being reverted?',
    },
    remove: {
      title: 'Remove from ITCL request',
      confirmLabel: 'Remove',
      variant: 'danger',
      body: 'This GC will leave this ITCL request, return to booked, and notify only the booking agent.',
      placeholder: 'Why is this GC being removed from the request?',
    },
    note: {
      title: 'Add note',
      confirmLabel: 'Save note',
      variant: 'primary',
      body: 'This note is sent to the booking agent. Unload status does not change.',
      placeholder: 'Enter note for the booking agent…',
    },
  }[action] || {
    title: 'Unload shipments',
    confirmLabel: 'Unload',
    variant: 'primary',
    body: 'A note is required.',
    placeholder: 'Note (required)',
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      onClick={() => !loading && onClose?.()}
      role="presentation"
    >
      <div
        className="bg-white w-full sm:max-w-md sm:rounded-xl rounded-t-2xl shadow-xl p-4 sm:p-5 max-h-[90vh] overflow-y-auto min-w-0"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ogcl-unload-note-title"
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <h3 id="ogcl-unload-note-title" className="text-base sm:text-lg font-semibold text-secondary">
              {copy.title}
            </h3>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              {ids.length} shipment{ids.length === 1 ? '' : 's'} selected
            </p>
          </div>
          <button
            type="button"
            disabled={loading}
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-3">{copy.body}</p>

        {ids.length > 0 && ids.length <= 8 && (
          <p className="text-xs text-gray-500 mb-3 break-all">{ids.join(', ')}</p>
        )}

        <label className="block text-sm font-medium text-secondary mb-1.5">
          Note
          <span className="text-red-500"> *</span>
        </label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          disabled={loading}
          required
          placeholder={copy.placeholder}
          className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm resize-y min-h-[88px] focus:outline-none focus:border-blue-400"
        />
        {!noteReady && (
          <p className="mt-1.5 text-xs text-amber-800">A note is required before you can continue.</p>
        )}

        <div className="mt-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <Button
            type="button"
            variant="secondary"
            disabled={loading}
            className="w-full sm:w-auto"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant={copy.variant}
            loading={loading}
            disabled={loading || !noteReady}
            className="w-full sm:w-auto"
            onClick={() => noteReady && onConfirm?.(note.trim())}
          >
            {copy.confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OgclUnloadNoteModal;
